import { useEffect, useState, useRef } from "react"; // Import useRef
import { useNavigate } from "react-router";

import dayjs from "dayjs";

import {
  Button,
  Table,
  DatePicker,
  Alert,
  Popconfirm,
  List,
  Input,
  Space,
} from "antd"; // Import Input, Space
import { SearchOutlined } from "@ant-design/icons"; // Import SearchOutlined

import useTransfer from "../hooks/useTransfer";
import useParseString from "../hooks/useParseString";
import PageDocumentation from "../components/PageDocumentation";

const djangoAdminUrl = `${process.env.REACT_APP_API_URL}admin/transfers/`;

const typeMap = {
  TRANSFER_PAYMENT: "Rendición",
  TRANSFER_PAYMENT__USER_SELLER: "Rendición vendedores",
  TRANSFER_PAYMENT__USER_GUIDE: "Rendiciones Micros",
  TRANSFER_PAYMENT__USER_COORDINATOR: "Rendiciones coordinadores",
  TRANSFER_PAYMENT__COMPANY: "Rendiciones companias",
  EXTRAORDINARY_TRANSACTION: "Gastos extraordinarios",
  PAYMENT: "Pagos de pasajeros a oficina",
};

function PaymentHistory() {
  const navigate = useNavigate();
  const searchInput = useRef(null); // Add ref for search input

  const { formatCurrency } = useParseString();

  const { payments, getPayments, getBoxesAmount, boxAmounts, loading } =
    useTransfer();
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);

  const [totalAmount, setTotalAmount] = useState(0);
  const [params, setParams] = useState({
    page: 1,
    start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    if (!payments || !payments.results) return;

    const totalAmount = payments.results.reduce(
      (acc, transfer) => (acc += transfer.amount),
      0
    );

    setTotalAmount(totalAmount);
  }, [payments]);

  useEffect(() => {
    if (!dateRange) return;

    const formattedStartDate = dateRange[0].format("YYYY-MM-DD");
    const formattedEndDate = dateRange[1].format("YYYY-MM-DD");

    setParams({
      ...params,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    });
  }, [dateRange]);

  useEffect(() => {
    getBoxesAmount(params);
    getPayments(params);
  }, [params]);

  const handleTodayClick = () => {
    setDateRange([dayjs().startOf("day"), dayjs().endOf("day")]);
  };

  const isToday =
    dateRange[0].isSame(dayjs().startOf("day"), "day") &&
    dateRange[1].isSame(dayjs().endOf("day"), "day");

  const handleTableChange = (pagination, filters) => {
    const paymentTypeFilter = filters?.paymentType || null;
    const receiverFilter = filters?.receiver?.[0] || null; // Get receiver filter value

    setParams((prevParams) => ({
      ...prevParams,
      page: pagination.current,
      paymentType: paymentTypeFilter,
      title: receiverFilter, // Add receiver filter to params
    }));
  };

  // Function to handle search confirmation
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };

  // Function to handle search reset
  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  // Function to generate column search props
  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Resetear
          </Button>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    // Note: We don't need onFilter here as filtering is done server-side via params
  });

  const columns = [
    {
      title: "Emisor",
      dataIndex: ["sender", "name"],
      key: "sender",
    },
    {
      title: "Receptor",
      dataIndex: ["receiver", "name"],
      key: "receiver",
      ...getColumnSearchProps("receiver", "Receptor"), // Add search props
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Método de pago",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (text, record) => {
        return record.paymentMethod
          ? `${record.paymentMethod?.name} - ${record.paymentMethod?.box}`
          : "No asignado";
      },
    },
    {
      title: "Tipo",
      dataIndex: "paymentType",
      key: "paymentType",
      filters: Object.entries(typeMap).map(([value, text]) => ({
        text,
        value,
      })),
      filterSearch: true,
      render: (text) => {
        return typeMap[text] || text;
      },
    },
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => dayjs(text).format("ddd DD MMM"),
    },
    {
      title: "Cantidad rendida",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => (
        <span className={text < 0 ? "text-red-500" : "text-green-500"}>
          {formatCurrency(text)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (text, record) => {
        return (
          <div className="flex flex-col">
            <Button
              type="link"
              onClick={() => {
                if (record.paymentType.includes("PASSENGER")) {
                  navigate(`/pasajeros/${record.relativeId}`);
                } else if (record.paymentType.includes("TRANSFER")) {
                  navigate(`/transfers/${record.relativeId}/detail`);
                } else {
                  window.open(
                    `${djangoAdminUrl}transaction/${record.relativeId}/change/`,
                    "_blank"
                  );
                }
              }}
            >
              Ver detalle
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-lg">Historial de Pagos</span>
        </div>
        <div className="flex items-center gap-2">
          <PageDocumentation docKey="payments-history" />
          <Button
            type="primary"
            onClick={() => {
              window.open(`${djangoAdminUrl}transaction/add/`, "_blank");
            }}
          >
            Agregar pago manual
          </Button>
        </div>
      </div>

      <div className="flex flex-col my-3">
        <span className="font-semibold text-md me-2">Filtros</span>

        <div className="">
          <DatePicker.RangePicker
            value={dateRange}
            onChange={setDateRange}
            allowClear={false}
            disabled={loading.get}
          />
          {!isToday && (
            <Button type="link" onClick={handleTodayClick}>
              Ver transferencias de hoy
            </Button>
          )}
        </div>
      </div>
      {isToday && (
        <Alert
          message="Estas viendo los pagos de Hoy"
          type="info"
          className="mb-3"
          showIcon
        />
      )}

      <List
        header={<div className="font-semibold">Balance por caja</div>}
        bordered
        loading={loading.get}
        className="my-3 bg-gray-50"
        dataSource={boxAmounts?.boxes}
        renderItem={(item) => (
          <List.Item>
            <div className="flex justify-between w-full">
              <span>{item.box.name}</span>
              <span
                className={
                  item.totalAmount < 0 ? "text-red-500" : "text-green-500"
                }
              >
                {formatCurrency(item.totalAmount)}
              </span>
            </div>
          </List.Item>
        )}
        footer={
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span
              className={
                boxAmounts.totalAmount < 0 ? "text-red-500" : "text-green-500"
              }
            >
              {formatCurrency(boxAmounts.totalAmount)}
            </span>
          </div>
        }
      />
      <Table
        dataSource={payments?.results}
        columns={columns}
        rowKey="id"
        loading={loading.get}
        pagination={{
          current: params.page,
          pageSize: payments?.results?.length,
          total: payments?.count,
        }}
        onChange={handleTableChange}
        footer={() => {
          return (
            <div className="flex justify-between">
              <span className="font-semibold">Total rendido</span>
              <span
                className={
                  totalAmount < 0
                    ? "text-red-500 font-bold"
                    : "text-green-500 font-bold"
                }
              >
                {formatCurrency(totalAmount)}
              </span>
            </div>
          );
        }}
      />
    </>
  );
}

export default PaymentHistory;
